/**
 * Menu Insights — two tabs.
 * Products: top/bottom sellers by revenue & quantity, modifier attach rate.
 * Categories: revenue by category and by department/tax type.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid,
} from 'recharts';
import { formatAmd, formatNumber } from '../locale.js';
import { supabase } from '../supabase.js';
import {
  Callout,
  CHART_TOOLTIP,
  chartColor,
  DateRange,
  PageHeader,
  resolveDateRangeDates,
  Tabs,
  type DateRangeValue,
} from '../ui/index.js';
import { enumerateDays, toDayKey } from '../inventoryPeriod.js';

type MenuTab = 'products' | 'categories';
type Period = DateRangeValue;

const TS = CHART_TOOLTIP;

function periodBounds(p: Period): { from: string; to: string; fromIso: string; toIso: string } {
  const [start, end] = resolveDateRangeDates(p);
  const pad = (n: number) => String(n).padStart(2, '0');
  const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { from: ymd(start), to: ymd(end), fromIso: start.toISOString(), toIso: end.toISOString() };
}

const TAX_LABEL_KEYS: Record<number, string> = {
  1: 'menuInsights.vatTaxable',
  2: 'menuInsights.vatExempt',
  3: 'menuInsights.turnoverTax',
};

const UNCATEGORISED = '__uncategorised__';

// ─── Types ────────────────────────────────────────────────────────────────────

const DAILY_DEFAULT_PRODUCTS = 8;

interface DailyRawRow {
  key: string;
  label: string;
  segments: Record<string, { revenue: number; qty: number }>;
}

interface DailyChartRow {
  key: string;
  label: string;
  totalRevenue: number;
  totalQty: number;
  segments: Record<string, { revenue: number; qty: number }>;
}

interface ProductStat {
  name: string;
  revenue: number;
  qty: number;
  modOrders: number;
  totalOrders: number;
}

interface CatStat {
  name: string;
  revenue: number;
}

interface DeptStat {
  label: string;
  revenue: number;
}

interface MenuData {
  products: ProductStat[];
  categories: CatStat[];
  departments: DeptStat[];
  dailyByProduct: DailyRawRow[];
  dailyByCategory: DailyRawRow[];
  productNames: string[];
  categoryNames: string[];
}

// ─── Fetch ────────────────────────────────────────────────────────────────────

/** PostgREST caps responses at 1000 rows — paginate in 1000-row chunks. */
const FETCH_PAGE = 1000;

async function fetchPaged<T>(
  query: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: { message: string } | null }>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let offset = 0; ; offset += FETCH_PAGE) {
    const { data, error } = await query(offset, offset + FETCH_PAGE - 1);
    if (error) throw new Error(error.message);
    const batch = data ?? [];
    rows.push(...batch);
    if (batch.length < FETCH_PAGE) break;
  }
  return rows;
}

function lineRevenue(item: { unit_price: number; quantity: number; discount: number | null }) {
  return Number(item.unit_price) * Number(item.quantity) - Number(item.discount ?? 0);
}

async function fetchMenu(clientId: string, period: Period): Promise<MenuData> {
  if (!supabase) throw new Error('__not_configured__');
  const db = supabase;
  const { from, to, fromIso, toIso } = periodBounds(period);

  type OrderItemRow = {
    name: string;
    unit_price: number;
    quantity: number;
    discount: number | null;
    department_id: number | null;
    modifiers: unknown;
    order_id: string;
    orders: { paid_at: string | null } | null;
  };

  const items = (await fetchPaged((offset, limit) =>
    db
      .from('order_items')
      .select(
        'name, unit_price, quantity, discount, department_id, modifiers, order_id, orders!inner(status, paid_at, client_id)',
      )
      .eq('client_id', clientId)
      .filter('orders.status', 'eq', 'paid')
      .filter('orders.paid_at', 'gte', fromIso)
      .filter('orders.paid_at', 'lte', toIso)
      .filter('orders.client_id', 'eq', clientId)
      .order('id')
      .range(offset, limit),
  )) as unknown as OrderItemRow[];

  const [catsRes, deptsRes] = await Promise.all([
    db
      .from('products')
      .select('name, category_id, categories!category_id(name)')
      .eq('client_id', clientId),

    db.from('departments').select('id, name, tax_type').eq('client_id', clientId),
  ]);

  const dayProductMap = new Map<string, Map<string, { revenue: number; qty: number }>>();
  const dayCategoryMap = new Map<string, Map<string, { revenue: number; qty: number }>>();
  const prodMap = new Map<
    string,
    { name: string; revenue: number; qty: number; orderIds: Set<string>; modOrderIds: Set<string> }
  >();

  const prodRows = (catsRes.data ?? []) as unknown as Array<{
    name: string;
    categories: { name: string } | null;
  }>;
  const nameToCat = new Map<string, string>();
  for (const p of prodRows) {
    if (p.categories?.name) nameToCat.set(p.name, p.categories.name);
  }

  for (const item of items) {
    const paidAt = item.orders?.paid_at;
    const rev = lineRevenue(item);
    const qty = Number(item.quantity);
    const hasMod = Array.isArray(item.modifiers) && (item.modifiers as unknown[]).length > 0;
    const catName = nameToCat.get(item.name) ?? UNCATEGORISED;

    if (paidAt) {
      const dayKey = toDayKey(paidAt);
      let day = dayProductMap.get(dayKey);
      if (!day) {
        day = new Map();
        dayProductMap.set(dayKey, day);
      }
      const pe = day.get(item.name) ?? { revenue: 0, qty: 0 };
      pe.revenue += rev;
      pe.qty += qty;
      day.set(item.name, pe);

      let dayCat = dayCategoryMap.get(dayKey);
      if (!dayCat) {
        dayCat = new Map();
        dayCategoryMap.set(dayKey, dayCat);
      }
      const ce = dayCat.get(catName) ?? { revenue: 0, qty: 0 };
      ce.revenue += rev;
      ce.qty += qty;
      dayCat.set(catName, ce);
    }

    let existing = prodMap.get(item.name);
    if (!existing) {
      existing = {
        name: item.name,
        revenue: 0,
        qty: 0,
        orderIds: new Set(),
        modOrderIds: new Set(),
      };
      prodMap.set(item.name, existing);
    }
    existing.revenue += rev;
    existing.qty += qty;
    existing.orderIds.add(item.order_id);
    if (hasMod) existing.modOrderIds.add(item.order_id);
  }
  const products: ProductStat[] = Array.from(prodMap.values())
    .map((p) => ({
      name: p.name,
      revenue: p.revenue,
      qty: p.qty,
      totalOrders: p.orderIds.size,
      modOrders: p.modOrderIds.size,
    }))
    .sort((a, b) => b.revenue - a.revenue);
  const productNames = products.map((p) => p.name);

  const dayKeys = enumerateDays(from, to);

  const dailyByProduct: DailyRawRow[] = dayKeys.map(({ key, label }) => ({
    key,
    label,
    segments: Object.fromEntries(dayProductMap.get(key) ?? []),
  }));

  const catTotals = new Map<string, number>();
  for (const item of items) {
    const cat = nameToCat.get(item.name) ?? UNCATEGORISED;
    catTotals.set(cat, (catTotals.get(cat) ?? 0) + lineRevenue(item));
  }
  const categoryNames = [...catTotals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name);

  const dailyByCategory: DailyRawRow[] = dayKeys.map(({ key, label }) => ({
    key,
    label,
    segments: Object.fromEntries(dayCategoryMap.get(key) ?? []),
  }));

  // ── Category stats — period totals by category
  const categories = Array.from(catTotals, ([name, revenue]) => ({
    name,
    revenue: Math.round(revenue),
  })).sort((a, b) => b.revenue - a.revenue);

  // ── Department stats
  const deptRows = deptsRes.data ?? [];
  const deptIdMap = new Map(deptRows.map((d) => [d.id, d]));
  const deptMap = new Map<string, number>();
  for (const item of items) {
    if (!item.department_id) continue;
    const dept = deptIdMap.get(item.department_id);
    const label = dept
      ? dept.name || (TAX_LABEL_KEYS[dept.tax_type] ? `__tax:${dept.tax_type}` : `__dept:${dept.id}`)
      : '__unknown__';
    deptMap.set(label, (deptMap.get(label) ?? 0) + lineRevenue(item));
  }
  const departments = Array.from(deptMap, ([label, revenue]) => ({
    label,
    revenue: Math.round(revenue),
  })).sort((a, b) => b.revenue - a.revenue);

  return {
    products,
    categories,
    departments,
    dailyByProduct,
    dailyByCategory,
    productNames,
    categoryNames,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function fmtQty(n: number, lang?: string | null) {
  return Number.isInteger(n)
    ? formatNumber(n, lang)
    : formatNumber(n, lang, { maximumFractionDigits: 1 });
}

function displayCatName(name: string, t: (k: string, o?: Record<string, unknown>) => string) {
  if (name === UNCATEGORISED) return t('menuInsights.uncategorised');
  if (name === '__unknown__') return t('menuInsights.unknownDept');
  if (name.startsWith('__tax:')) {
    const id = Number(name.slice(6));
    const key = TAX_LABEL_KEYS[id];
    return key ? t(key) : name;
  }
  if (name.startsWith('__dept:')) {
    return t('menuInsights.deptPrefix', { id: name.slice(7) });
  }
  return name;
}

function fmtAxisRevenue(n: number) {
  const v = Math.round(n);
  if (v >= 1_000_000) return `${Math.round(v / 1_000_000)}M`;
  if (v >= 1000) return `${Math.round(v / 1000)}k`;
  return String(v);
}

function fmtAxisQty(n: number) {
  const v = Number(n);
  if (v >= 1000) return `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function buildDailyChart(
  raw: DailyRawRow[],
  allNames: string[],
  selected: Set<string>,
): { rows: DailyChartRow[]; series: string[] } {
  const series = allNames.filter((n) => selected.has(n));
  const rows = raw.map((row) => {
    const segments: Record<string, { revenue: number; qty: number }> = {};
    for (const name of series) {
      segments[name] = row.segments[name] ?? { revenue: 0, qty: 0 };
    }
    const totalRevenue = Object.values(segments).reduce((s, v) => s + v.revenue, 0);
    const totalQty = Object.values(segments).reduce((s, v) => s + v.qty, 0);
    return {
      key: row.key,
      label: row.label,
      totalRevenue: Math.round(totalRevenue),
      totalQty,
      segments,
    };
  });
  return { rows, series };
}

function seriesColor(name: string, allNames: string[]): string {
  const i = allNames.indexOf(name);
  return chartColor(i >= 0 ? i : 0);
}

function DailyTooltipContent({
  dayLabel,
  name,
  color,
  amount,
  pct,
}: {
  dayLabel: string;
  name: string;
  color: string;
  amount: string;
  pct: number;
}) {
  const { t } = useTranslation();
  return (
    <div className="chart-tooltip">
      {dayLabel && <div className="chart-tooltip-title">{dayLabel}</div>}
      <div className="chart-tooltip-row">
        <span className="chart-tooltip-dot" style={{ background: color }} />
        <span className="chart-tooltip-name">{name}</span>
      </div>
      <div className="chart-tooltip-amount">{amount}</div>
      <div className="chart-tooltip-pct">{t('menuInsights.pctOfDay', { pct })}</div>
    </div>
  );
}

type SegmentHover = {
  dayLabel: string;
  name: string;
  value: number;
  pct: number;
  color: string;
  x: number;
  y: number;
};

function SeriesFilter({
  label,
  allNames,
  selected,
  periodTotals,
  onChange,
}: {
  label: string;
  allNames: string[];
  selected: Set<string>;
  periodTotals: Map<string, number>;
  onChange: (next: Set<string>) => void;
}) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const visible = search.trim()
    ? allNames.filter((n) =>
        displayCatName(n, t).toLowerCase().includes(search.trim().toLowerCase()),
      )
    : allNames;

  function toggle(name: string) {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onChange(next);
  }

  return (
    <div className="menu-series-filter" ref={rootRef}>
      <button type="button" className="btn-ghost btn-sm menu-series-filter-toggle" onClick={() => setOpen((v) => !v)}>
        {label}: {t('menuInsights.includeCount', { selected: selected.size, total: allNames.length })}
      </button>
      {open && (
        <div className="menu-series-filter-panel">
          <div className="menu-series-filter-actions">
            <input
              className="input menu-series-filter-search"
              type="search"
              placeholder={t('common.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="button" className="btn-ghost btn-sm" onClick={() => onChange(new Set(allNames))}>
              {t('menuInsights.filterAll')}
            </button>
            <button type="button" className="btn-ghost btn-sm" onClick={() => onChange(new Set())}>
              {t('menuInsights.filterNone')}
            </button>
          </div>
          <ul className="menu-series-filter-list">
            {visible.map((name) => (
              <li key={name}>
                <label className="menu-series-filter-item">
                  <input type="checkbox" checked={selected.has(name)} onChange={() => toggle(name)} />
                  <span className="menu-series-filter-dot" style={{ background: seriesColor(name, allNames) }} />
                  <span className="menu-series-filter-name">{displayCatName(name, t)}</span>
                  <span className="menu-series-filter-val muted">{AMD(periodTotals.get(name) ?? 0)}</span>
                </label>
              </li>
            ))}
            {visible.length === 0 && (
              <li className="muted menu-series-filter-empty">{t('menuInsights.noMatches')}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function DailySalesChart({
  title,
  raw,
  allNames,
  selected,
  onSelectionChange,
  periodTotals,
}: {
  title: string;
  raw: DailyRawRow[];
  allNames: string[];
  selected: Set<string>;
  onSelectionChange: (next: Set<string>) => void;
  periodTotals: Map<string, number>;
}) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const [mode, setMode] = useState<'revenue' | 'qty'>('revenue');
  const [segmentHover, setSegmentHover] = useState<SegmentHover | null>(null);
  const { rows, series } = buildDailyChart(raw, allNames, selected);
  const xInterval = rows.length > 20 ? ('preserveStartEnd' as const) : rows.length > 10 ? 2 : 0;

  const chartData = useMemo(
    () =>
      rows.map((row) => ({
        label: row.label,
        dayTotal: mode === 'revenue' ? row.totalRevenue : row.totalQty,
        ...Object.fromEntries(
          series.map((name) => [
            name,
            mode === 'revenue'
              ? Math.round(row.segments[name]?.revenue ?? 0)
              : row.segments[name]?.qty ?? 0,
          ]),
        ),
      })),
    [rows, series, mode],
  );

  function showSegmentTooltip(
    row: (typeof chartData)[number],
    name: string,
    e: React.MouseEvent<Element, MouseEvent>,
  ) {
    const v = Number(row[name as keyof typeof row]);
    if (!v) return;
    const dayTotal = Number(row.dayTotal);
    setSegmentHover({
      dayLabel: row.label,
      name,
      value: v,
      pct: dayTotal > 0 ? Math.round((v / dayTotal) * 100) : 0,
      color: seriesColor(name, allNames),
      x: e.clientX,
      y: e.clientY,
    });
  }

  function barDataIndex(entry: { originalDataIndex?: number }, fallbackIndex: number) {
    return entry.originalDataIndex ?? fallbackIndex;
  }

  const periodTotal = chartData.reduce((s, d) => s + Number(d.dayTotal), 0);
  const hasAnySales = raw.some((d) => Object.keys(d.segments).length > 0);

  useEffect(() => {
    setSegmentHover(null);
  }, [mode, selected, raw]);

  if (!hasAnySales) {
    return (
      <div className="chart-card insights-wide" style={{ marginBottom: 16 }}>
        <div className="chart-title">{title}</div>
        <p className="chart-empty">{t('menuInsights.noSales')}</p>
      </div>
    );
  }

  return (
    <div className="chart-card insights-wide" style={{ marginBottom: 16 }}>
      <div className="menu-daily-chart-head">
        <span className="chart-title">{title}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <SeriesFilter
            label={t('menuInsights.include')}
            allNames={allNames}
            selected={selected}
            periodTotals={periodTotals}
            onChange={onSelectionChange}
          />
          <div className="segment-ctrl" style={{ fontSize: 11 }}>
            <button
              type="button"
              className={mode === 'revenue' ? 'active' : ''}
              onClick={() => setMode('revenue')}
            >
              {t('menuInsights.modeRevenue')}
            </button>
            <button type="button" className={mode === 'qty' ? 'active' : ''} onClick={() => setMode('qty')}>
              {t('menuInsights.modeQuantity')}
            </button>
          </div>
        </div>
      </div>

      {series.length === 0 ? (
        <p className="chart-empty">{t('menuInsights.selectItem')}</p>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 8, left: 4, bottom: 0 }}
              onMouseLeave={() => setSegmentHover(null)}
            >
              <CartesianGrid
                horizontal
                vertical={false}
                stroke="var(--border)"
                strokeOpacity={0.6}
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 10, fill: 'var(--text-subtle)' }}
                interval={xInterval}
              />
              <YAxis
                width={mode === 'revenue' ? 44 : 36}
                tickCount={5}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 10, fill: 'var(--text-subtle)' }}
                tickFormatter={(v) =>
                  mode === 'revenue' ? fmtAxisRevenue(Number(v)) : fmtAxisQty(Number(v))
                }
              />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                formatter={(value: string) => {
                  const label = displayCatName(value, t);
                  return label.length > 22 ? `${label.slice(0, 20)}…` : label;
                }}
              />
              {series.map((name) => (
                <Bar
                  key={name}
                  dataKey={name}
                  name={displayCatName(name, t)}
                  stackId="daily"
                  fill={seriesColor(name, allNames)}
                  maxBarSize={32}
                  isAnimationActive={false}
                  activeBar={{ stroke: '#fff', strokeWidth: 1, fillOpacity: 0.9 }}
                  onMouseEnter={(entry, index, e) => {
                    const row = chartData[barDataIndex(entry, index)];
                    if (row) showSegmentTooltip(row, name, e);
                  }}
                  onMouseMove={(entry, index, e) => {
                    const row = chartData[barDataIndex(entry, index)];
                    if (row) showSegmentTooltip(row, name, e);
                  }}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
          {segmentHover &&
            createPortal(
              <div
                className="chart-tooltip-floating"
                style={{ left: segmentHover.x + 14, top: segmentHover.y + 14 }}
              >
                <DailyTooltipContent
                  dayLabel={segmentHover.dayLabel}
                  name={displayCatName(segmentHover.name, t)}
                  color={segmentHover.color}
                  amount={
                    mode === 'revenue'
                      ? AMD(segmentHover.value)
                      : fmtQty(segmentHover.value, i18n.language)
                  }
                  pct={segmentHover.pct}
                />
              </div>,
              document.body,
            )}
          <p className="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
            {t('menuInsights.chartTotal', {
              amount:
                mode === 'revenue' ? AMD(periodTotal) : fmtQty(periodTotal, i18n.language),
              count: series.length,
            })}
          </p>
        </>
      )}
    </div>
  );
}

function ProductsTab({ products }: { products: ProductStat[] }) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  const [sort, setSort] = useState<'revenue' | 'qty'>('revenue');
  if (!products.length) return <p className="chart-empty">{t('menuInsights.noSales')}</p>;

  const sorted = [...products].sort((a, b) =>
    sort === 'revenue' ? b.revenue - a.revenue : b.qty - a.qty,
  );
  const top = sorted.slice(0, 12);
  const bottom = sorted.slice(-5).reverse();
  const maxVal = Math.max(...top.map((p) => (sort === 'revenue' ? p.revenue : p.qty)), 1);
  const totalRev = products.reduce((s, p) => s + p.revenue, 0);

  return (
    <div className="insights-grid">
      {/* Top products */}
      <div className="chart-card insights-wide">
        <div
          className="chart-title"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span>{t('menuInsights.topProducts')}</span>
          <div className="segment-ctrl" style={{ fontSize: 11 }}>
            <button
              className={sort === 'revenue' ? 'active' : ''}
              onClick={() => setSort('revenue')}
            >
              {t('menuInsights.byRevenue')}
            </button>
            <button className={sort === 'qty' ? 'active' : ''} onClick={() => setSort('qty')}>
              {t('menuInsights.byQty')}
            </button>
          </div>
        </div>
        <div className="top-list">
          {top.map((p, i) => {
            const val = sort === 'revenue' ? p.revenue : p.qty;
            return (
              <div key={p.name} className="top-row">
                <span className="top-rank">{i + 1}</span>
                <div className="top-info">
                  <div className="top-name">{p.name}</div>
                  <div className="top-bar-track">
                    <div className="top-bar-fill" style={{ width: `${(val / maxVal) * 100}%` }} />
                  </div>
                </div>
                <div className="top-meta">
                  <span className="top-revenue">
                    {sort === 'revenue' ? AMD(p.revenue) : `×${p.qty.toFixed(1)}`}
                  </span>
                  <span className="muted" style={{ fontSize: 11 }}>
                    {totalRev > 0 ? `${Math.round((p.revenue / totalRev) * 100)}%` : ''}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom sellers */}
      <div className="chart-card">
        <div className="chart-title">{t('menuInsights.bottomSellers')}</div>
        <div className="top-list">
          {bottom.map((p) => (
            <div key={p.name} className="top-row">
              <div className="top-info">
                <div className="top-name">{p.name}</div>
              </div>
              <div className="top-meta">
                <span className="top-revenue">{AMD(p.revenue)}</span>
                <span className="muted" style={{ fontSize: 11 }}>
                  ×{p.qty.toFixed(1)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CategoriesTab({
  categories,
  departments,
}: {
  categories: CatStat[];
  departments: DeptStat[];
}) {
  const { t, i18n } = useTranslation();
  const AMD = (n: number) => formatAmd(n, i18n.language);
  if (!categories.length) return <p className="chart-empty">{t('menuInsights.noSales')}</p>;

  const catChart = categories.map((c) => ({ ...c, name: displayCatName(c.name, t) }));

  return (
    <div className="insights-grid">
      {/* Category bar chart */}
      <div className="chart-card insights-wide">
        <div className="chart-title">{t('menuInsights.revenueByCategory')}</div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart
            data={catChart}
            layout="vertical"
            margin={{ top: 4, right: 60, left: 8, bottom: 0 }}
          >
            <XAxis type="number" hide />
            <YAxis
              type="category"
              dataKey="name"
              width={110}
              tick={{ fontSize: 12, fill: 'var(--text-muted)' }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              formatter={(v: unknown) => [AMD(Number(v)), t('menuInsights.tooltipRevenue')]}
              contentStyle={TS}
              cursor={{ fill: 'var(--surface-hover)' }}
            />
            <Bar
              dataKey="revenue"
              radius={[0, 4, 4, 0]}
              maxBarSize={22}
              label={{
                position: 'right',
                fontSize: 11,
                fill: 'var(--text-muted)',
                formatter: (v: unknown) => AMD(v as number),
              }}
            >
              {catChart.map((_, i) => (
                <Cell key={i} fill={chartColor(i)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Category pie */}
      <div className="chart-card">
        <div className="chart-title">{t('menuInsights.categoryShare')}</div>
        {categories.length > 0 && (
          <>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={catChart}
                  cx="50%"
                  cy="50%"
                  outerRadius={68}
                  dataKey="revenue"
                  paddingAngle={2}
                >
                  {catChart.map((_, i) => (
                    <Cell key={i} fill={chartColor(i)} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: unknown) => [AMD(Number(v)), '']} contentStyle={TS} />
              </PieChart>
            </ResponsiveContainer>
            <div className="pay-legend" style={{ marginTop: 4 }}>
              {categories.slice(0, 6).map((c, i) => {
                const total = categories.reduce((s, x) => s + x.revenue, 0);
                return (
                  <div key={c.name} className="pay-legend-row">
                    <span
                      className="pay-legend-dot"
                      style={{ background: chartColor(i) }}
                    />
                    <span className="pay-legend-name">{displayCatName(c.name, t)}</span>
                    <span className="pay-legend-pct">
                      {total > 0 ? Math.round((c.revenue / total) * 100) : 0}%
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Department breakdown */}
      {departments.length > 0 && (
        <div className="chart-card">
          <div className="chart-title">{t('menuInsights.revenueByDept')}</div>
          <div className="quality-list">
            {departments.map((d) => (
              <div key={d.label} className="quality-row">
                <span className="quality-label">{displayCatName(d.label, t)}</span>
                <span className="quality-value">{AMD(d.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

export function MenuInsightsView({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<MenuTab>('products');
  const [period, setPeriod] = useState<Period>({ preset: '30d' });
  const [data, setData] = useState<MenuData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productSelection, setProductSelection] = useState<Set<string>>(new Set());
  const [categorySelection, setCategorySelection] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!clientId) return;
    if (period.preset === 'custom' && (!period.from || !period.to)) return;
    setLoading(true);
    setError(null);
    fetchMenu(clientId, period)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : t('menuInsights.loadFailed');
        setError(msg === '__not_configured__' ? t('menuInsights.notConfigured') : msg);
        setLoading(false);
      });
  }, [clientId, period]);

  useEffect(() => {
    if (!data) return;
    setProductSelection(new Set(data.productNames.slice(0, DAILY_DEFAULT_PRODUCTS)));
    setCategorySelection(new Set(data.categoryNames));
  }, [data, period]);

  const productPeriodTotals = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of data?.products ?? []) m.set(p.name, p.revenue);
    return m;
  }, [data]);

  const categoryPeriodTotals = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of data?.categories ?? []) m.set(c.name, c.revenue);
    return m;
  }, [data]);

  return (
    <div className="sales-page">
      <PageHeader
        title={t('menuInsights.title')}
        subtitle={t('menuInsights.subtitle')}
        actions={
          <DateRange value={period} onChange={setPeriod} />
        }
      />

      <Tabs
        value={tab}
        onChange={setTab}
        options={[
          { value: 'products', label: t('menuInsights.tabProducts') },
          { value: 'categories', label: t('menuInsights.tabCategories') },
        ]}
      />

      {loading && (
        <div className="dash-loading">
          <div className="spinner" /> {t('common.loading')}
        </div>
      )}
      {error && (
        <Callout tone="danger" title={t('menuInsights.couldntLoad')}>
          {error}
        </Callout>
      )}
      {!loading && data && tab === 'products' && (
        <>
          <DailySalesChart
            title={t('menuInsights.dailyByItem')}
            raw={data.dailyByProduct}
            allNames={data.productNames}
            selected={productSelection}
            onSelectionChange={setProductSelection}
            periodTotals={productPeriodTotals}
          />
          <ProductsTab products={data.products} />
        </>
      )}
      {!loading && data && tab === 'categories' && (
        <>
          <DailySalesChart
            title={t('menuInsights.dailyByCategory')}
            raw={data.dailyByCategory}
            allNames={data.categoryNames}
            selected={categorySelection}
            onSelectionChange={setCategorySelection}
            periodTotals={categoryPeriodTotals}
          />
          <CategoriesTab categories={data.categories} departments={data.departments} />
        </>
      )}
    </div>
  );
}
